import { useCallback, useEffect, useState } from 'react';
import { appendCampaignToUrl } from '#/utils/campaignContext';
import { track } from '#/utils/analytics';
import { iconUrl } from '#/utils/assetUrl';
import { DEEPLINK_APP_ENTRY } from '#/constants/deeplinks';
import { useBenefitDetail } from '#/domains/preview/hooks/useBenefitDetail';
import { useOverlayBackClose } from '#/domains/preview/hooks/useOverlayBackClose';
import type { BenefitDetail } from '#/domains/preview/api/govBenefitApi';

interface Props {
  serviceSeqNo: string;
  serviceName: string;
  onClose: () => void;
}

export default function BenefitDetailModal({
  serviceSeqNo,
  serviceName,
  onClose,
}: Props) {
  const [showSheet, setShowSheet] = useState(false);
  const { detail, isLoading, error } = useBenefitDetail(serviceSeqNo);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showSheet) setShowSheet(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showSheet]);

  const closeInnerSheet = useCallback(() => setShowSheet(false), []);
  useOverlayBackClose(showSheet, closeInnerSheet);

  const ctaHref = appendCampaignToUrl(DEEPLINK_APP_ENTRY);

  const handleClickWall = () => {
    if (showSheet) return;
    setShowSheet(true);
    track('gov_benefit_all_preview_landing_detail_open', {
      service_seq_no: serviceSeqNo,
    });
  };

  const handleCtaClick = () => {
    track('service_conversion_sub', {
      event_key: 'benefits_preview_detail_cta',
    });
  };

  return (
    <div className="bp-modal" role="dialog" aria-modal="true">
      <header className="bp-modal-header">
        <button
          type="button"
          className="bp-modal-close"
          onClick={onClose}
          aria-label="닫기"
        >
          ✕
        </button>
        <span className="bp-modal-title" title={serviceName}>
          {serviceName}
        </span>
        <span className="bp-modal-header-spacer" aria-hidden />
      </header>

      <div className="bp-modal-body">
        <div className="bp-detail">
          {isLoading && <DetailSkeleton />}
          {error && !isLoading && (
            <div className="bp-detail-error">
              <p>{error}</p>
            </div>
          )}
          {detail && !isLoading && (
            <DetailContent
              detail={detail}
              fallbackName={serviceName}
              onUnlock={handleClickWall}
            />
          )}
        </div>
      </div>

      {showSheet && (
        <>
          <div
            className="bp-sheet-backdrop"
            onClick={() => setShowSheet(false)}
            aria-hidden
          />
          <div className="bp-sheet" role="dialog" aria-modal="true">
            <div className="bp-sheet-handle" />
            <h3 className="bp-sheet-title">
              더 상세한 정보와 맞춤 정부 혜택은
              <br />
              <span className="bp-sheet-title-emph">
                혜택알리미에서 확인해보세요
              </span>
            </h3>
            <p className="bp-sheet-sub">
              모든 혜택의 자격 조건을 하나하나 따져보긴 너무 어려워요.
              <br />
              삼쩜삼 혜택알리미가 대신 찾아드릴게요.
            </p>

            <div className="bp-sheet-features">
              <div className="bp-sheet-feature">
                <img
                  src={iconUrl('feat-magnifier.png')}
                  alt=""
                  className="bp-sheet-feature-icon"
                />
                <div>
                  <h4 className="bp-sheet-feature-title">
                    내 상황에 딱 맞는 혜택만
                  </h4>
                  <p className="bp-sheet-feature-desc">
                    나이, 지역, 직업, 가구 상황을 분석해서 받을 수 있는 혜택만
                    콕 집어 알려드려요.
                  </p>
                </div>
              </div>
              <div className="bp-sheet-feature">
                <img
                  src={iconUrl('feat-bell-alarm.png')}
                  alt=""
                  className="bp-sheet-feature-icon"
                />
                <div>
                  <h4 className="bp-sheet-feature-title">
                    새로운 혜택, 놓치지 않게 알림
                  </h4>
                  <p className="bp-sheet-feature-desc">
                    새 혜택이 생기면 푸시로 알려드려요. 신청 기간 놓칠 일
                    없어요.
                  </p>
                </div>
              </div>
              <div className="bp-sheet-feature">
                <img
                  src={iconUrl('feat-hand-check.png')}
                  alt=""
                  className="bp-sheet-feature-icon"
                />
                <div>
                  <h4 className="bp-sheet-feature-title">
                    확인부터 신청까지 한 번에
                  </h4>
                  <p className="bp-sheet-feature-desc">
                    복잡한 정부 사이트 돌아다닐 필요 없이 삼쩜삼 앱 하나로
                    끝나는 혜택도 있어요.
                  </p>
                </div>
              </div>
            </div>

            <a
              id="btn-detail-cta"
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCtaClick}
              className="bp-sheet-cta"
            >
              맞춤 혜택 확인하기
            </a>
            <button
              type="button"
              className="bp-sheet-cancel"
              onClick={() => setShowSheet(false)}
            >
              닫기
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DetailContent({
  detail,
  fallbackName,
  onUnlock,
}: {
  detail: BenefitDetail;
  fallbackName: string;
  onUnlock: () => void;
}) {
  const title = detail.name || fallbackName;

  const infoChips: Array<{ label: string; value: string }> = [
    { label: '신청기간', value: detail.applicationDeadline },
    { label: '소관기관', value: detail.jurisdictionOrganization },
    { label: '지원형태', value: detail.supportFormat },
    { label: '신청방식', value: detail.applicationProcessType },
  ].filter(c => c.value && c.value.trim());

  return (
    <>
      <div className="bp-detail-hero">
        <div className="bp-detail-hero-icon">
          {detail.imageUrl ? (
            <img src={detail.imageUrl} alt="" loading="lazy" />
          ) : (
            <span>혜택</span>
          )}
        </div>
        <h2 className="bp-detail-title">{title}</h2>
        {detail.introduction && (
          <p className="bp-detail-intro">{detail.introduction}</p>
        )}
      </div>

      {infoChips.length > 0 && (
        <ul className="bp-detail-info">
          {infoChips.map(c => (
            <li key={c.label} className="bp-detail-info-item">
              <span className="bp-detail-info-label">{c.label}</span>
              <span className="bp-detail-info-value">{c.value}</span>
            </li>
          ))}
        </ul>
      )}

      <DetailSection title="지원 대상" body={detail.targetAudience} />
      <DetailSection title="선정 기준" body={detail.selectionCriteria} />
      <DetailSection title="지원 내용" body={detail.content} />

      <div className="bp-detail-unlock-row">
        <button type="button" className="bp-detail-unlock" onClick={onUnlock}>
          <svg
            className="bp-detail-unlock-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <rect
              x="5"
              y="11"
              width="14"
              height="9"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M8 11V8a4 4 0 1 1 8 0v3"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          더보기
        </button>
      </div>

      <DetailSection
        title="신청 방법"
        body={detail.applicationProcess}
        blurred
      />
      <DetailSection
        title="구비 서류"
        body={detail.requiredDocuments}
        blurred
      />

      {detail.legalBases.length > 0 && (
        <section className="bp-detail-section bp-detail-section--blurred">
          <h3 className="bp-detail-section-title">법령</h3>
          <ul className="bp-detail-laws">
            {detail.legalBases.map(law => (
              <li key={law.lawInfoSequence} className="bp-detail-law">
                <span className="bp-detail-law-tag">{law.lawTypeName}</span>
                <span className="bp-detail-law-name">{law.lawKoreanName}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(detail.referenceName || detail.referencePhoneNo) && (
        <section className="bp-detail-section bp-detail-section--blurred">
          <h3 className="bp-detail-section-title">문의처</h3>
          <p className="bp-detail-paragraph">
            {[detail.referenceName, detail.referencePhoneNo]
              .filter(Boolean)
              .join(' ')}
          </p>
        </section>
      )}

      {detail.modifiedDate && (
        <p className="bp-detail-updated bp-detail-section--blurred">
          최종 수정일 {detail.modifiedDate}
        </p>
      )}
    </>
  );
}

function DetailSection({
  title,
  body,
  blurred = false,
}: {
  title: string;
  body: string;
  blurred?: boolean;
}) {
  if (!body || !body.trim()) return null;
  return (
    <section
      className={
        blurred
          ? 'bp-detail-section bp-detail-section--blurred'
          : 'bp-detail-section'
      }
    >
      <h3 className="bp-detail-section-title">{title}</h3>
      <p className="bp-detail-paragraph">{body}</p>
    </section>
  );
}

function DetailSkeleton() {
  return (
    <div className="bp-detail-skeleton" aria-hidden>
      <div className="bp-skeleton-box bp-detail-skeleton-icon" />
      <div className="bp-skeleton-line bp-skeleton-line--lg" />
      <div className="bp-skeleton-line bp-skeleton-line--md" />
      <div className="bp-skeleton-line bp-skeleton-line--sm" />
      <div className="bp-detail-skeleton-chips">
        <div className="bp-skeleton-box bp-detail-skeleton-chip" />
        <div className="bp-skeleton-box bp-detail-skeleton-chip" />
      </div>
      <div className="bp-skeleton-line bp-skeleton-line--md" />
      <div className="bp-skeleton-line bp-skeleton-line--md" />
      <div className="bp-skeleton-line bp-skeleton-line--sm" />
    </div>
  );
}
